import {
  HTMLAttributes,
  forwardRef,
  useState,
  ReactNode,
  Ref,
  ChangeEvent,
  KeyboardEvent,
  MutableRefObject,
} from 'react';
import classNames from 'classnames';
import NumberFormat, { NumberFormatValues, SourceInfo } from 'react-number-format';
import { IconName } from 'components/atoms/Icon/all';
import Icon from 'components/atoms/Icon';
import emojiRegex from 'emoji-regex';
import { Configs } from 'utils/constants';
import { getMessageCredits } from 'utils';
import MessageCreditsInline from 'components/molecules/MessageCreditsInline';
import { isFunction } from 'utils/common';
import Typography from 'components/atoms/Typography';
import { twMerge } from 'tailwind-merge';

const PERCENT_CLOSE_TO_LIMIT = 0.8;

export function ExplainerText({
  text,
  toggleLabel,
  isAbove,
}: {
  text: ReactNode;
  toggleLabel?: ReactNode;
  isAbove?: boolean;
}) {
  const [explainerOpen, setExplainerOpen] = useState(!toggleLabel);

  return (
    <>
      {toggleLabel && (
        <div
          className="text-xs underline text-glooMedGray cursor-pointer p-2"
          onClick={() => setExplainerOpen(!explainerOpen)}
        >
          {toggleLabel}
        </div>
      )}
      {explainerOpen ? (
        <div
          className={classNames('text-xs text-glooMedGray pl-2 italic', {
            'pt-2': !isAbove && !toggleLabel,
            'pb-1': isAbove,
          })}
        >
          {text}
        </div>
      ) : null}
    </>
  );
}

export interface InputProps extends HTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  allowEmptyFormatting?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
  characterLimit?: number;
  cols?: number;
  containerStyles?: string;
  disabled?: boolean;
  displayMessageCredits?: boolean;
  emojiCharacterLimit?: number;
  error?: string;
  explainerText?: ReactNode;
  explainerToggleLabel?: ReactNode;
  extraContent?: ReactNode;
  format?: string;
  hideCharLimitUnlessClose?: boolean;
  icon?: IconName;
  iconClassNames?: string;
  isAutoGrow?: boolean;
  isClearable?: boolean;
  isFormattedNumber?: boolean;
  isMultiLine?: boolean;
  label?: string | ReactNode;
  mask?: string;
  max?: string | number;
  maxLength?: number;
  min?: string | number;
  name?: string;
  onNumberFormatValueChange?: (values: NumberFormatValues, sourceInfo: SourceInfo) => void;
  onChangeText?: (t: string, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  onCtrlEnter?: (e?: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  onEnter?: (e?: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
  onLimitChange?: (b: boolean) => any;
  required?: boolean;
  rows?: number;
  showCreditsHelpLink?: boolean;
  step?: string | number;
  subLabel?: string;
  textColor?: string;
  type?: string;
  value?: string | number;
  dataDurableId?: string;
  placeholder?: string;
}

const Input = forwardRef(
  (
    {
      allowEmptyFormatting,
      autoComplete,
      autoFocus,
      characterLimit,
      className,
      cols,
      containerStyles,
      defaultValue,
      disabled,
      displayMessageCredits,
      emojiCharacterLimit,
      error,
      explainerText,
      explainerToggleLabel,
      extraContent,
      format,
      hideCharLimitUnlessClose,
      icon,
      iconClassNames,
      isClearable,
      isFormattedNumber,
      isMultiLine,
      label,
      mask,
      max,
      min,
      onChange,
      onChangeText,
      onCtrlEnter,
      onEnter,
      onLimitChange,
      onNumberFormatValueChange,
      required,
      rows,
      showCreditsHelpLink,
      step,
      subLabel,
      textColor,
      type,
      value,
      dataDurableId,
      placeholder,
      ...rest
    }: InputProps,
    ref: Ref<HTMLInputElement | HTMLTextAreaElement> | MutableRefObject<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const [hasEmoji, setHasEmoji] = useState(false);
    const [isOverLimit, setIsOverLimit] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue || '');

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value || '';
      const newHasEmoji = emojiCharacterLimit && emojiRegex().test(text);

      if (emojiCharacterLimit && newHasEmoji !== hasEmoji) {
        setHasEmoji(newHasEmoji);
      }

      if ((characterLimit && text.length > characterLimit) || (newHasEmoji && text.length > emojiCharacterLimit)) {
        if (isOverLimit === false) {
          if (isFunction(onLimitChange)) onLimitChange(true);
          setIsOverLimit(true);
        }
      } else if (
        isOverLimit &&
        ((characterLimit && text.length <= characterLimit) || (newHasEmoji && text.length <= emojiCharacterLimit))
      ) {
        if (isFunction(onLimitChange)) onLimitChange(false);
        setIsOverLimit(false);
      }

      if (onChangeText) {
        onChangeText(text, e);
      } else if (onChange) {
        onChange(e);
      } else {
        setInternalValue(text);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement> | KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && onEnter) {
        e.preventDefault();
        e.stopPropagation();
        onEnter(e);
      } else if (onCtrlEnter && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopPropagation();
        onCtrlEnter(e);
      }
    };

    const charLimit = emojiCharacterLimit && hasEmoji ? emojiCharacterLimit : characterLimit;
    let displayCharLimit = !!charLimit;
    const valueStr = !onChangeText && !onChange ? (internalValue as string) : (value || '').toString();

    if (displayCharLimit && hideCharLimitUnlessClose) {
      if (valueStr.length < charLimit * PERCENT_CLOSE_TO_LIMIT) {
        displayCharLimit = false;
      }
    }

    const messageCreditsUsed = getMessageCredits(valueStr);

    return (
      <div
        className={classNames(
          'flex flex-col items-start justify-center w-full',
          { relative: isClearable },
          containerStyles,
        )}
      >
        {icon ? (
          <Icon name={icon} className={classNames('ml-2 absolute z-[1] text-glooMedGray', iconClassNames)} size={18} />
        ) : null}
        {(label || subLabel) && (
          <div className="pb-2 pl-1">
            {label && (
              <Typography variant="label">
                {label}
                {required && <span className="text-red text-xs">*</span>}
              </Typography>
            )}
            {subLabel && (
              <Typography className="text-glooMedGray" variant="base">
                {subLabel}
              </Typography>
            )}
          </div>
        )}
        <div className="flex flex-col w-full relative">
          {isFormattedNumber ? (
            <NumberFormat
              ref={ref as any}
              autoFocus={autoFocus}
              autoComplete={autoComplete}
              type={(type as any) || 'text'}
              onKeyDown={onEnter || onCtrlEnter ? handleKeyDown : undefined}
              // maxLength={characterLimit}
              value={value === null ? '' : value}
              format={format}
              allowEmptyFormatting={allowEmptyFormatting}
              mask={mask}
              disabled={disabled}
              className={twMerge(
                'appearance-none rounded-sm w-full py-2 px-3 text-glooCharcoal leading-tight focus:outline-none text-sm border border-charcoal-300 bg-white',
                classNames(
                  { 'opacity-50': disabled },
                  { 'pl-8': !!icon },
                  { 'border-red': !!error },
                  { 'pr-6': isClearable },
                ),
                className,
              )}
              maxLength={rest.maxLength}
              onValueChange={onNumberFormatValueChange}
              name={rest.name}
              data-durable-id={dataDurableId}
              data-intercom-target={dataDurableId}
            />
          ) : isMultiLine ? (
            <textarea
              ref={ref as Ref<HTMLTextAreaElement>}
              onKeyDown={onEnter || onCtrlEnter ? handleKeyDown : undefined}
              autoFocus={autoFocus}
              autoComplete={autoComplete}
              defaultValue={defaultValue}
              rows={rows}
              cols={cols}
              placeholder={placeholder}
              {...rest}
              className={twMerge(
                'appearance-none rounded-sm w-full py-2 px-3 leading-tight focus:outline-none text-sm border border-charcoal-300 bg-white',
                textColor || 'text-glooCharcoal',
                classNames({ 'opacity-50': disabled }, { 'pl-8': !!icon }, { 'border-red border': !!error }),
                className,
              )}
              onChange={handleChange as any}
              value={value === null ? '' : value}
              disabled={disabled}
              data-durable-id={dataDurableId || 'inputTextAreaLinkId'}
              data-intercom-target={dataDurableId || 'inputTextAreaLinkId'}
            />
          ) : (
            <input
              ref={ref as Ref<HTMLInputElement>}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              className={twMerge(
                'appearance-none rounded-sm w-full py-2 px-3 leading-tight focus:outline-none text-sm placeholder:text-glooLightGray border border-charcoal-300 bg-white',
                textColor || 'text-glooCharcoal',
                classNames(
                  { 'opacity-50': disabled },
                  { 'pl-8': !!icon },
                  { 'border-red border': !!error },
                  { 'pr-7': isClearable },
                ),
                className,
              )}
              defaultValue={defaultValue}
              disabled={disabled}
              onKeyDown={onEnter || onCtrlEnter ? handleKeyDown : undefined}
              type={type || 'text'}
              value={value === null ? '' : value}
              {...rest}
              max={type === 'number' ? max : undefined}
              min={type === 'number' ? min : undefined}
              onChange={handleChange}
              step={type === 'number' ? step : undefined}
              data-durable-id={dataDurableId || 'inputLinkId'}
              data-intercom-target={dataDurableId || 'inputLinkId'}
              placeholder={placeholder}
            />
          )}
          {isClearable && valueStr.length > 0 ? (
            <div
              className={classNames(
                'absolute top-0 bottom-0 right-0 flex flex-row items-center p-3 cursor-pointer animate100 animated fadeInRight',
              )}
              onClick={() => handleChange({ target: { value: '' } } as any)}
            >
              <Icon name="x" className="text-glooMedGray" size={18} />
            </div>
          ) : null}
          {error ? <div className="text-xs text-red p-2">{error}</div> : null}
          {explainerText && <ExplainerText text={explainerText} toggleLabel={explainerToggleLabel} />}
          <div className="flex justify-end">
            {displayCharLimit ? (
              <div
                className={classNames('text-xs text-glooMedGray py-1 px-1 w-full text-right', {
                  'text-red': isOverLimit,
                })}
              >
                {hasEmoji && emojiCharacterLimit && (
                  <span className="text-yellow mr-2">Emojis change your character limit</span>
                )}
                {showCreditsHelpLink ? (
                  <a
                    data-durable-id="broadCastCreditLinkId"
                    data-intercom-target="broadCastCreditLinkId"
                    href={Configs.Help.HowTo.BroadcastCredits}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames('hover:opacity-80 hover:underline')}
                  >
                    <>
                      {valueStr.length}/{charLimit}
                    </>
                  </a>
                ) : (
                  <>
                    {valueStr.length}/{charLimit}
                  </>
                )}
              </div>
            ) : null}

            {displayMessageCredits ? <MessageCreditsInline credits={messageCreditsUsed} className="pl-4" /> : null}
          </div>
        </div>
        {extraContent}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
